import React, { FormEvent, ChangeEvent } from "react";
import { Prompt, RouteComponentProps } from "react-router";

import DeletionConfirmationDialog from "../DeletionConfirmationDialog";

import { V2 } from "../../Api";
import withTitle from "../withTitle";
import ValidateInput from "../ValidateInput";
import ViewToolbar from "../ViewToolbar";
import OverflowMenuItem from "../OverflowMenuItem";

interface ISurveyEditViewState {
    isEdit: boolean;
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    hasChanged: boolean;

    isDeleteOpen: boolean;

    questionPool: any[];

    title: string;
    questions: any[];

    newQuestion: string;

    [name: string]: any;
}

class SurveyEditView extends React.Component<RouteComponentProps<any>, ISurveyEditViewState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: ISurveyEditViewState = {
            isEdit: !!props.match.params.id,
            isLoading: true,
            isSubmitting: false,
            hasChanged: false,

            isDeleteOpen: false,

            questionPool: [],

            title: "",
            questions: [],

            newQuestion: "",
        };

        this.state = state;

        this.inputs = [];

        this.submitForm = this.submitForm.bind(this);
        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.addQuestion = this.addQuestion.bind(this);
        this.deleteQuestion = this.deleteQuestion.bind(this);
        this.handleQuestionChange = this.handleQuestionChange.bind(this);
        this.cancelEdit = this.cancelEdit.bind(this);

        this.closeDeletionDialog = this.closeDeletionDialog.bind(this);
        this.showDelete = this.showDelete.bind(this);
        this.confirmDelete = this.confirmDelete.bind(this);
    }

    public async componentDidMount() {
        try {
            const survey = this.state.isEdit
                ? await V2.get<any>(`survey-types/${this.props.match.params.id}`)
                : { title: "", questions: [] };
            const questions = await V2.get<any[]>("survey-types/questions");

            this.setState({
                isLoading: false,

                title: survey.title,
                questions: survey.questions,

                questionPool: questions
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load survey"
            });
        }
    }

    private receiveValidationRef(ref: ValidateInput) {
        this.inputs.push(ref);
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root"><div className="spinner spinner-center lds-dual-ring-big" /></div>;
        }

        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root"><div className="error-box">{this.state.isLoading}</div></div>;
        }

        return (
            <div className="viewer-root with-actionbar">
                <ViewToolbar>
                    <OverflowMenuItem>
                        <button type="button" onClick={this.showDelete}><i className="icon-trash" /><span>Delete survey</span></button>
                    </OverflowMenuItem>
                </ViewToolbar>
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <DeletionConfirmationDialog isOpen={this.state.isDeleteOpen} description="Are you sure you want to delete this survey?" onRequestClose={this.closeDeletionDialog} onConfirm={this.confirmDelete} />
                <form className="input-form" onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Survey Info</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-title">Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-title" name="title" value={this.state.title} onChange={this.formInputChanged} placeholder="Survey title" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                        </section>
                        <section className="section-spacer" />
                    </div>
                    &nbsp;
                    <table className="section-table table-edit table-vertical nohover">
                        <thead>
                            <tr>
                                <th>Question</th>
                                <th className="col-smallest">Type</th>
                                <th className="col-smallest" />
                            </tr>
                        </thead>
                        <thead className="table-vertical-head-small">
                            <tr>
                                <th colSpan={10}>Questions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                this.state.questions.map((a, i) => <tr key={i}>
                                    <td className="col-nopad">
                                        <select name="questionId" value={a.questionId} onChange={e => this.handleQuestionChange(e, i)}>
                                            {this.generateQuestionOptions()}
                                        </select>
                                    </td>
                                    <td className="pad">{this.questionTypeToString(a.type)}</td>
                                    <td className="col-nopad">
                                        <button type="button" className="button smaller btn-light red" title="Delete" onClick={() => this.deleteQuestion(i)}><i className="icon-times" /></button>
                                    </td>
                                </tr>)
                            }
                            <tr>
                                <td className="col-nopad">
                                    <select name="newQuestion" value={this.state.newQuestion} onChange={this.formInputChanged}>
                                        <option />
                                        {this.generateQuestionOptions()}
                                    </select>
                                </td>
                                <td />
                                <td className="col-nopad">
                                    <button type="button" className="button smaller btn-light" title="Add" onClick={this.addQuestion} disabled={!this.state.newQuestion}><i className="icon-plus" /></button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    &nbsp;
                    {this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <div className="error-text">{this.state.isSubmitting}</div>
                            : null
                    }
                    <div className="action-panel">
                        <button type="submit" className="button push-right">Save</button>
                        <button type="button" className="button" onClick={this.cancelEdit}>Cancel</button>
                    </div>
                </form>
            </div>
        );
    }

    private async submitForm(e: FormEvent) {
        e.preventDefault();

        let isAllValid = true;
        for (const input of this.inputs) {
            isAllValid = input.validate() && isAllValid;
        }

        if (!isAllValid) {
            return;
        }

        try {
            this.setState({
                isSubmitting: true
            });

            const data = {
                title: this.state.title,

                questions: this.state.questions
            };

            if (this.state.isEdit) {
                await V2.patch(`survey-types/${this.props.match.params.id}`, data);
            }
            else {
                await V2.post("survey-types", data);
            }

            this.setState({
                hasChanged: false,
                isSubmitting: false
            }, () => this.cancelEdit());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e || "Could not save"
            });
        }
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        let name = e.currentTarget.name;
        let value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            hasChanged: true,

            [name]: value
        });
    }

    private cancelEdit() {
        this.props.history.goBack();
    }

    private generateQuestionOptions() {
        return this.state.questionPool.map(a =>
            <option key={a.id} value={a.id} disabled={this.state.questions.some(b => b.questionId === a.id)}>{a.text}</option>
        );
    }

    private handleQuestionChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>, index: number) {
        const name = e.currentTarget.name;
        let value: string | number = e.currentTarget.value;

        const questions = this.state.questions;
        questions[index][name] = value;

        this.setState({
            hasChanged: true,

            questions
        });
    }

    private deleteQuestion(index: number) {
        const questions = this.state.questions;
        questions.splice(index, 1);

        this.setState({
            hasChanged: true,

            questions
        });
    }

    private addQuestion() {
        const questions = this.state.questions;
        const question = this.state.questionPool.find(a => a.id === this.state.newQuestion);
        questions.push({
            questionId: question.id,
            type: question.type
        });

        this.setState({
            hasChanged: true,

            newQuestion: "",

            questions
        });
    }

    private questionTypeToString(type: string) {
        switch (type) {
            case "yesno": return "Yes/No"
            default: return type;
        }
    }

    private closeDeletionDialog() {
        this.setState({
            isDeleteOpen: false,
        });
    }

    private showDelete() {
        this.setState({
            isDeleteOpen: true
        });
    }

    private async confirmDelete() {
        await V2.delete(`survey-types/${this.props.match.params.id}`);

        this.setState({
            hasChanged: false
        }, () => this.cancelEdit());
    }
}

export default withTitle("Survey Details", SurveyEditView);
